/**
 * Team Design — Quick Customization banner swatches.
 *
 * Reads the collection's `custom.quick_customization_code` metafield (passed
 * via the panel's `data-collection-code` data attribute by the section liquid),
 * fetches the collection-scoped color theme list from
 * /api/squad/rest/collection-quick-customization/<code>, and renders
 * up to 12 selectable swatches in the banner panel + 4 color chips
 * (C1..C4 = primary/secondary/tertiary/accent of the active theme).
 *
 * If the metafield is missing the section liquid suppresses the panel
 * entirely. If the API returns no themes (or errors), this script removes
 * the panel at runtime so the sidebar slot doesn't reserve empty space.
 *
 * Click flow:
 *   1. Mark the swatch active, update C1..C4 chips.
 *   2. Persist the picked theme on window.CollectionTeamText.state.bannerTheme
 *      so team-design.swatches.js#restore() falls back to this theme when a
 *      per-card hover ends (instead of swatches[0]).
 *   3. For every card in the grid, fetch its first pattern thumbnail (via
 *      shared designCache + SvgEngine cache), colorize with the banner theme,
 *      and render into the card's preview overlay — same code path as the
 *      per-card hover, just persistent + grid-wide.
 *
 * Depends on:
 *   - window.TeamDesignSvg            (svg-engine.js)
 *   - window.CollectionDesignOptions  (swatches.js — must load first)
 *   - window.CollectionTeamText.state (text-render.js — for shared state)
 */
(function () {
  'use strict';

  // API defaults stashed at fetch time. We do NOT auto-apply them on
  // page load — the picker stays neutral until the shopper interacts.
  // First interaction (typing a team name, clicking a color, clicking a
  // font) promotes these into state.bannerTextFont + state.bannerNumberFont
  // + state.bannerPlayerNameFont / state.bannerTheme via selectDefaultFont()
  // and the click handlers.
  //
  // apiDefaultFontPair is shaped `{ textFont, numberFont, playerNameFont }`,
  // where each slot carries `{ id, family, fontUrl }`. The pair is the unit
  // the picker selects on now (one button = one pair) — the previous flat
  // `fonts[]` shape was replaced server-side by `fontPairs[]` so the
  // admin can configure a separate font for letters (team), the player
  // name, and numbers within a single picker tile.
  var apiDefaultFontPair = null;
  var apiDefaultTheme    = null;

  function readState() {
    var ns = (window.CollectionTeamText = window.CollectionTeamText || {});
    ns.state = ns.state || {};
    return ns.state;
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;');
  }

  // Reorder a list of {id, ...} items to match an ordered id array. The
  // collection-quick-customization API returns the curated drag-and-drop
  // order in `colorThemeIds` (with the configured default at position
  // 0), but the enriched `colorThemes` array may come back in
  // db/alphabetical order. Re-sorting client-side guarantees the
  // picker shows the admin's exact order regardless of API ordering.
  // (Font pairs use the API's array order directly — they aren't
  // individually identified, so there's nothing to id-match against.)
  function sortByIdOrder(items, orderedIds) {
    if (!orderedIds || !orderedIds.length) return items.slice();
    var byId = {};
    for (var i = 0; i < items.length; i++) byId[items[i].id] = items[i];
    var ordered = [];
    var seen = {};
    for (var j = 0; j < orderedIds.length; j++) {
      var id = orderedIds[j];
      if (byId[id] && !seen[id]) {
        ordered.push(byId[id]);
        seen[id] = true;
      }
    }
    for (var k = 0; k < items.length; k++) {
      if (!seen[items[k].id]) ordered.push(items[k]);
    }
    return ordered;
  }

  // ─── Render ──────────────────────────────────────────────────────────────
  // Each swatch mirrors the calculator's Step 1 swatch (libs/frontend/calculator):
  // top row = primary (flex: 2), bottom row split into secondary + tertiary/accent.
  // Selected state gets a 2px black border via .is-active in CSS.
  //
  // A "colorful" pseudo-theme sits at index 0. Clicking it clears
  // state.bannerTheme so every card reverts to its own first-swatch
  // colors (the per-card jersey design) — there's no other way back to
  // that state once a real theme has been picked.
  //
  // It boots WITHOUT `.is-active` — same gate the font picker uses:
  // we only mark a selection once SVGs are actually rendered on the
  // cards (i.e., the shopper has expressed intent via team text /
  // logo / font). markColorfulActiveIfDefault() promotes it to active
  // from those same trigger sites.
  function colorfulSwatchHtml() {
    // 3×3 grid of brand-mixed colors so the swatch reads as "many colors"
    // at a glance. Three `--split` rows give equal thirds vertically; each
    // row has THREE inner spans so `.qc-swatch__row--split > span { flex:
    // 1 1 0 }` carves them into equal thirds horizontally.
    return (
      '<button type="button" class="qc-swatch qc-swatch--colorful"' +
      ' data-color-full="1"' +
      ' aria-label="Use each product\'s own colors">' +
        '<span class="qc-swatch__row qc-swatch__row--split">' +
          '<span style="background-color:#1E997D;"></span>' +
          '<span style="background-color:#A9DDE3;"></span>' +
          '<span style="background-color:#FAD1E1;"></span>' +
        '</span>' +
        '<span class="qc-swatch__row qc-swatch__row--split">' +
          '<span style="background-color:#FE564B;"></span>' +
          '<span style="background-color:#FDD400;"></span>' +
          '<span style="background-color:#F3E7DB;"></span>' +
        '</span>' +
        '<span class="qc-swatch__row qc-swatch__row--split">' +
          '<span style="background-color:#FC71CC;"></span>' +
          '<span style="background-color:#FA9359;"></span>' +
          '<span style="background-color:#1D5EDE;"></span>' +
        '</span>' +
      '</button>'
    );
  }

  function renderSwatches(container, themes) {
    var themeHtml = themes.map(function (t, i) {
      // Two-row swatch per design: primary fills the top 32px block and
      // secondary fills a slim 8px band underneath. Tertiary / accent are
      // still carried on the data-* attrs so downstream code (chip
      // updates, custom color picker) keeps working — they just aren't
      // painted in the swatch preview anymore.
      var primary   = t.primaryColor   || '#ccc';
      var secondary = t.secondaryColor || t.primaryColor || '#ccc';
      return (
        '<button type="button" class="qc-swatch"' +
        ' data-index="'     + i + '"' +
        ' data-theme-code="' + escapeAttr(t.code) + '"' +
        ' data-primary="'   + escapeAttr(t.primaryColor)   + '"' +
        ' data-secondary="' + escapeAttr(t.secondaryColor) + '"' +
        ' data-tertiary="'  + escapeAttr(t.tertiaryColor)  + '"' +
        ' data-accent="'    + escapeAttr(t.accentColor)    + '"' +
        ' aria-label="Color theme ' + (i + 1) + '">' +
          '<span class="qc-swatch__row qc-swatch__row--top" style="background-color:' + escapeAttr(primary) + ';"></span>' +
          '<span class="qc-swatch__row qc-swatch__row--bottom" style="background-color:' + escapeAttr(secondary) + ';"></span>' +
        '</button>'
      );
    }).join('');
    // Colorful pseudo-theme hidden for now — see quick-customization.liquid.
    // container.innerHTML = colorfulSwatchHtml() + themeHtml;
    container.innerHTML = themeHtml;
  }

  function updateChips(theme) {
    var roles = {
      primary:   theme.primaryColor   || '#e5e5e5',
      secondary: theme.secondaryColor || '#e5e5e5',
      tertiary:  theme.tertiaryColor  || '#e5e5e5',
      accent:    theme.accentColor    || '#ffffff',
    };
    var chips = document.querySelectorAll('#qc-chips .qc-chip');
    for (var i = 0; i < chips.length; i++) {
      var role = chips[i].getAttribute('data-color-role');
      var sw   = chips[i].querySelector('.qc-chip__swatch');
      if (sw && roles[role]) sw.style.backgroundColor = roles[role];
    }
  }

  function setActiveSwatch(container, activeEl) {
    var all = container.querySelectorAll('.qc-swatch');
    for (var i = 0; i < all.length; i++) {
      all[i].classList.toggle('is-active', all[i] === activeEl);
    }
  }

  // ─── Apply theme to a single card ────────────────────────────────────────
  // Fetches the card's first pattern (matched by SKU's PS:<n> param if present)
  // from the shared designCache, then colorizes its SVG with the banner theme
  // and renders into the card's preview overlay. The text/logo MutationObserver
  // in text.js picks up the innerHTML write and re-injects overlays.
  function applyThemeToCard(card, theme) {
    var ApiHelpers = window.CollectionDesignOptions;
    var SvgEngine  = window.TeamDesignSvg;
    if (!ApiHelpers || !SvgEngine) return;

    var parsed = ApiHelpers.parseSku(card.dataset.sku || '');
    if (!parsed.code) return;

    ApiHelpers.fetch(parsed.code).then(function (data) {
      if (!data) return;
      var patterns = data.bundlePatternSets;
      if (!patterns || !patterns.length) return;

      var matched = null;
      if (parsed.ps !== null) {
        for (var i = 0; i < patterns.length; i++) {
          if (String(patterns[i].code) === String(parsed.ps)) { matched = patterns[i]; break; }
        }
      }
      if (!matched) matched = patterns[0];
      var thumbnail = matched.thumbnail;
      if (!thumbnail) return;

      var frame = card.querySelector('.card-product-figma__image-frame');
      if (!frame) return;
      if (window.getComputedStyle(frame).position === 'static') frame.style.position = 'relative';

      var mediaWrap = card.querySelector('.card-product-figma__image-frame .media--transparent');
      var previewEl = card.querySelector('.card-design-preview');
      if (!previewEl) {
        previewEl = document.createElement('div');
        previewEl.className = 'card-design-preview';
        frame.appendChild(previewEl);
      }
      if (!mediaWrap) return;

      SvgEngine.fetch(thumbnail).then(function (svgText) {
        if (!svgText) return;
        mediaWrap.style.visibility = 'hidden';
        previewEl.innerHTML        = SvgEngine.colorize(svgText, theme);
        previewEl.dataset.autoLoaded = '1';
        previewEl.style.display    = 'block';
      });
    });
  }

  function applyThemeToAllCards(theme) {
    var cards = document.querySelectorAll('.card-wrapper[data-sku]');
    for (var i = 0; i < cards.length; i++) applyThemeToCard(cards[i], theme);
  }

  // Force-loads each card's SVG preview using its OWN first-swatch theme
  // (the per-card jersey colors). Called when the shopper clicks the
  // colorful swatch — that click is treated as an explicit intent
  // signal, parallel to picking a font / real color theme / typing a
  // team name, so every card should paint a jersey even if no other
  // customization has happened yet.
  //
  // Cards that already have an SVG rendered are repainted with their
  // per-card theme (this is the "undo a real theme pick" path); cards
  // without an SVG get one loaded for the first time. Both branches
  // go through applyThemeToCard, which handles both cases.
  function applyPerCardThemes() {
    var cards = document.querySelectorAll('.card-wrapper[data-sku]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var swatch = card.querySelector('.card-color-swatch');
      if (!swatch) continue;

      applyThemeToCard(card, {
        primaryColor:   swatch.dataset.primary   || '',
        secondaryColor: swatch.dataset.secondary || '',
        tertiaryColor:  swatch.dataset.tertiary  || '',
        accentColor:    swatch.dataset.accent    || '',
      });
    }
  }

  // ─── Custom color picker (per-role chip → HSV/hue/hex/RGB popover) ───────
  // Vanilla-JS port of the calculator's Step 1 picker. Opens an absolutely
  // positioned popover near the tapped chip, drives the C1..C4 role colors
  // on state.bannerTheme, and repaints every card via the existing
  // applyThemeToAllCards pipeline. The picker DOM lives on <body> so it
  // escapes the QC sidebar's `overflow: hidden` and z-index stacking.

  var ROLE_TO_THEME_KEY = {
    primary:   'primaryColor',
    secondary: 'secondaryColor',
    tertiary:  'tertiaryColor',
    accent:    'accentColor',
  };
  var DEFAULT_CUSTOM_HEX = '#000000';
  var COMMIT_DELAY_MS = 160;

  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  function isCompleteHex(value) {
    return /^#?([0-9A-F]{3}|[0-9A-F]{6})$/i.test(String(value).trim());
  }

  function normalizeHex(value, fallback) {
    if (!isCompleteHex(value)) return fallback;
    var v = String(value).trim();
    if (v.charAt(0) !== '#') v = '#' + v;
    if (v.length === 4) {
      v = '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
    }
    return v.toUpperCase();
  }

  function hexToRgb(hex) {
    var n = normalizeHex(hex, DEFAULT_CUSTOM_HEX);
    return {
      r: parseInt(n.slice(1, 3), 16),
      g: parseInt(n.slice(3, 5), 16),
      b: parseInt(n.slice(5, 7), 16),
    };
  }

  function rgbToHex(rgb) {
    return '#' + [rgb.r, rgb.g, rgb.b].map(function (c) {
      var s = clamp(Math.round(c), 0, 255).toString(16);
      return s.length === 1 ? '0' + s : s;
    }).join('').toUpperCase();
  }

  function rgbToHsv(rgb) {
    var r = clamp(rgb.r, 0, 255) / 255;
    var g = clamp(rgb.g, 0, 255) / 255;
    var b = clamp(rgb.b, 0, 255) / 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var d = max - min;
    var h = 0;
    if (d !== 0) {
      if (max === r)      h = 60 * (((g - b) / d) % 6);
      else if (max === g) h = 60 * ((b - r) / d + 2);
      else                h = 60 * ((r - g) / d + 4);
    }
    return {
      h: h < 0 ? h + 360 : h,
      s: max === 0 ? 0 : d / max,
      v: max,
    };
  }

  function hsvToRgb(hsv) {
    var h = ((hsv.h % 360) + 360) % 360;
    var s = clamp(hsv.s, 0, 1);
    var v = clamp(hsv.v, 0, 1);
    var c = v * s;
    var seg = h / 60;
    var x = c * (1 - Math.abs((seg % 2) - 1));
    var m = v - c;
    var r = 0, g = 0, b = 0;
    if (seg < 1)       { r = c; g = x; }
    else if (seg < 2)  { r = x; g = c; }
    else if (seg < 3)  { g = c; b = x; }
    else if (seg < 4)  { g = x; b = c; }
    else if (seg < 5)  { r = x; b = c; }
    else               { r = c; b = x; }
    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  }

  var picker = {
    root: null, area: null, thumb: null, hue: null,
    hex: null, rIn: null, gIn: null, bIn: null,
    hsv: { h: 0, s: 0, v: 0 },
    activeChip: null,
    activeRole: null,
    commitTimer: null,
    dismissHandler: null,
    keyHandler: null,
    reflowHandler: null,
  };

  function clearScheduledCommit() {
    if (picker.commitTimer !== null) {
      window.clearTimeout(picker.commitTimer);
      picker.commitTimer = null;
    }
  }

  function commitTheme() {
    clearScheduledCommit();
    var state = readState();
    if (state.bannerTheme) {
      // Custom-color pick (HSV / hex / RGB) is the same "arm" intent as
      // tapping a preset swatch — keep newly-scrolled-in cards painting
      // from the IntersectionObserver path even after the immediate
      // applyThemeToAllCards write.
      var nsArm = window.CollectionTeamText && window.CollectionTeamText.arm;
      if (nsArm) nsArm();
      applyThemeToAllCards(state.bannerTheme);
    }
  }

  function scheduleCommit() {
    clearScheduledCommit();
    picker.commitTimer = window.setTimeout(function () {
      picker.commitTimer = null;
      commitTheme();
    }, COMMIT_DELAY_MS);
  }

  // Writes the picker's HSV/hex/RGB inputs from the active role's color
  // on state.bannerTheme. Called when the picker opens and after every
  // commit that wasn't initiated by the picker's own controls.
  function syncPickerFromState() {
    var state = readState();
    var theme = state.bannerTheme || {};
    var key = ROLE_TO_THEME_KEY[picker.activeRole];
    var activeHex = normalizeHex(theme[key], DEFAULT_CUSTOM_HEX);
    var rgb = hexToRgb(activeHex);
    var nextHsv = rgbToHsv(rgb);

    // Preserve hue when saturation is zero (gray) so the color area's
    // current column doesn't snap to red on a fresh open. Same trick the
    // calculator uses for keeping the editor stable across grayscale
    // values.
    var stableHue = ((picker.hsv.h % 360) + 360) % 360;
    picker.hsv = (nextHsv.s === 0 && stableHue)
      ? { h: stableHue, s: nextHsv.s, v: nextHsv.v }
      : nextHsv;

    picker.area.style.setProperty('--qc-color-hue', String(Math.round(picker.hsv.h)));
    picker.thumb.style.left = (picker.hsv.s * 100) + '%';
    picker.thumb.style.top  = ((1 - picker.hsv.v) * 100) + '%';
    picker.hue.value = String(Math.round(picker.hsv.h));
    picker.hex.value = activeHex;
    picker.rIn.value = String(rgb.r);
    picker.gIn.value = String(rgb.g);
    picker.bIn.value = String(rgb.b);
  }

  // Updates state.bannerTheme[activeRole] to the new hex and repaints
  // both the chip strip and every card. `preserveHue` keeps the picker's
  // current HSV anchor so dragging on the color area or the hue slider
  // doesn't visually fight the pointer.
  function previewColor(nextHex, preserveHue) {
    var state = readState();
    var theme = state.bannerTheme || {};
    var key = ROLE_TO_THEME_KEY[picker.activeRole];
    if (!key) return;

    var nextTheme = {
      // No longer a registered preset — clear `code` so any consumer that
      // reads it (analytics, future "reset to preset" UI) can tell this
      // is a customized theme.
      code:           '',
      primaryColor:   theme.primaryColor   || '',
      secondaryColor: theme.secondaryColor || '',
      tertiaryColor:  theme.tertiaryColor  || '',
      accentColor:    theme.accentColor    || '',
    };
    nextTheme[key] = normalizeHex(nextHex, theme[key] || DEFAULT_CUSTOM_HEX);
    state.bannerTheme = nextTheme;
    updateChips(nextTheme);

    // The theme is no longer one of the registered presets — deselect
    // any swatch that was showing `.is-active`, otherwise the preset row
    // would keep highlighting a tile whose colors don't match what the
    // chips and cards now show.
    var swatches = document.querySelectorAll('#qc-swatches .qc-swatch.is-active');
    for (var i = 0; i < swatches.length; i++) {
      swatches[i].classList.remove('is-active');
    }

    // Keep the above-grid color pill in sync with the custom colors so it
    // still reflects "a color is selected" (and its ✕ still clears it).
    renderColorPill(nextTheme);

    if (preserveHue) {
      var rgb = hexToRgb(nextTheme[key]);
      picker.area.style.setProperty('--qc-color-hue', String(Math.round(picker.hsv.h)));
      picker.thumb.style.left = (picker.hsv.s * 100) + '%';
      picker.thumb.style.top  = ((1 - picker.hsv.v) * 100) + '%';
      picker.hex.value = nextTheme[key];
      picker.rIn.value = String(rgb.r);
      picker.gIn.value = String(rgb.g);
      picker.bIn.value = String(rgb.b);
    } else {
      syncPickerFromState();
    }

    scheduleCommit();
  }

  function buildPickerDom() {
    if (picker.root) return picker.root;

    var root = document.createElement('div');
    root.className = 'qc-color-picker';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Custom color editor');
    root.hidden = true;

    var area = document.createElement('div');
    area.className = 'qc-color-picker__area';
    var thumb = document.createElement('span');
    thumb.className = 'qc-color-picker__area-thumb';
    area.appendChild(thumb);
    root.appendChild(area);

    var hue = document.createElement('input');
    hue.type = 'range';
    hue.min = '0';
    hue.max = '360';
    hue.step = '1';
    hue.className = 'qc-color-picker__hue';
    hue.setAttribute('aria-label', 'Hue');
    root.appendChild(hue);

    var fields = document.createElement('div');
    fields.className = 'qc-color-picker__fields';

    function buildField(labelText, type, className) {
      var label = document.createElement('label');
      label.className = 'qc-color-picker__field' + (className ? ' ' + className : '');
      var labelEl = document.createElement('span');
      labelEl.className = 'qc-color-picker__field-label';
      labelEl.textContent = labelText;
      var input = document.createElement('input');
      input.type = type;
      input.className = 'qc-color-picker__field-input';
      input.autocomplete = 'off';
      input.spellcheck = false;
      label.appendChild(labelEl);
      label.appendChild(input);
      fields.appendChild(label);
      return input;
    }

    var hex = buildField('Hex', 'text', 'is-hex');
    hex.maxLength = 7;
    var rIn = buildField('R', 'number');
    var gIn = buildField('G', 'number');
    var bIn = buildField('B', 'number');
    [rIn, gIn, bIn].forEach(function (i) {
      i.min = '0';
      i.max = '255';
      i.step = '1';
      i.inputMode = 'numeric';
    });
    root.appendChild(fields);

    document.body.appendChild(root);

    picker.root  = root;
    picker.area  = area;
    picker.thumb = thumb;
    picker.hue   = hue;
    picker.hex   = hex;
    picker.rIn   = rIn;
    picker.gIn   = gIn;
    picker.bIn   = bIn;

    bindPickerControls();
    return root;
  }

  function bindPickerControls() {
    // ─── Color area drag (HSV saturation × value) ──────────────────
    function applyPointer(e) {
      var rect = picker.area.getBoundingClientRect();
      var x = clamp(e.clientX - rect.left, 0, rect.width);
      var y = clamp(e.clientY - rect.top,  0, rect.height);
      picker.hsv = {
        h: picker.hsv.h,
        s: rect.width  ? x / rect.width  : 0,
        v: rect.height ? 1 - y / rect.height : 0,
      };
      previewColor(rgbToHex(hsvToRgb(picker.hsv)), true);
    }
    picker.area.addEventListener('pointerdown', function (e) {
      e.preventDefault();
      applyPointer(e);
      var move = function (ev) { applyPointer(ev); };
      var end  = function () {
        window.removeEventListener('pointermove',   move);
        window.removeEventListener('pointerup',     end);
        window.removeEventListener('pointercancel', end);
        commitTheme();
      };
      window.addEventListener('pointermove',   move);
      window.addEventListener('pointerup',     end);
      window.addEventListener('pointercancel', end);
    });

    // ─── Hue slider ────────────────────────────────────────────────
    picker.hue.addEventListener('input', function () {
      picker.hsv = {
        h: Number(picker.hue.value),
        s: picker.hsv.s,
        v: picker.hsv.v,
      };
      previewColor(rgbToHex(hsvToRgb(picker.hsv)), true);
    });
    picker.hue.addEventListener('change', commitTheme);

    // ─── Hex input ─────────────────────────────────────────────────
    function commitHexInput(restoreOnFail) {
      if (isCompleteHex(picker.hex.value)) {
        previewColor(normalizeHex(picker.hex.value, DEFAULT_CUSTOM_HEX), false);
        if (restoreOnFail) commitTheme();
        return;
      }
      if (restoreOnFail) {
        clearScheduledCommit();
        syncPickerFromState();
      }
    }
    picker.hex.addEventListener('input', function () {
      if (isCompleteHex(picker.hex.value)) commitHexInput(false);
    });
    picker.hex.addEventListener('blur', function () { commitHexInput(true); });
    picker.hex.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); commitHexInput(true); }
    });

    // ─── RGB inputs ────────────────────────────────────────────────
    function commitRgbInputs(restoreOnFail) {
      var rv = parseInt(picker.rIn.value, 10);
      var gv = parseInt(picker.gIn.value, 10);
      var bv = parseInt(picker.bIn.value, 10);
      if (isNaN(rv) || isNaN(gv) || isNaN(bv)) {
        if (restoreOnFail) { clearScheduledCommit(); syncPickerFromState(); }
        return;
      }
      previewColor(rgbToHex({
        r: clamp(rv, 0, 255),
        g: clamp(gv, 0, 255),
        b: clamp(bv, 0, 255),
      }), false);
      if (restoreOnFail) commitTheme();
    }
    [picker.rIn, picker.gIn, picker.bIn].forEach(function (inp) {
      inp.addEventListener('input', function () { commitRgbInputs(false); });
      inp.addEventListener('blur',  function () { commitRgbInputs(true); });
    });
  }

  // Positions the picker near the active chip. Uses `position: fixed`
  // coordinates so the popover stays anchored to the visible chip even
  // if the page scrolls (the QC sidebar is fixed on mobile and sticky on
  // desktop — both stay in the viewport while the document scrolls
  // underneath).
  function positionPicker() {
    var root = picker.root;
    var chip = picker.activeChip;
    if (!root || !chip) return;
    var anchor   = chip.getBoundingClientRect();
    var rootRect = root.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var inset = 16;
    var gap = 8;

    var top = anchor.bottom + gap;
    // Flip above if below would clip the bottom of the viewport.
    if (top + rootRect.height > vh - inset && anchor.top - rootRect.height - gap >= inset) {
      top = anchor.top - rootRect.height - gap;
    }
    top = clamp(top, inset, vh - rootRect.height - inset);

    var left = anchor.left + anchor.width / 2 - rootRect.width / 2;
    left = clamp(left, inset, vw - rootRect.width - inset);

    root.style.top  = top  + 'px';
    root.style.left = left + 'px';
  }

  function openColorPicker(chip) {
    var root = buildPickerDom();
    // Highlight the chip whose picker is open — reuses the existing
    // `.qc-chip.is-active` style (2px black border). Clear any previously
    // marked chip first so switching between chips doesn't leave a
    // stale highlight behind.
    var prev = document.querySelectorAll('#qc-chips .qc-chip.is-active');
    for (var p = 0; p < prev.length; p++) prev[p].classList.remove('is-active');
    chip.classList.add('is-active');

    picker.activeChip = chip;
    picker.activeRole = chip.getAttribute('data-color-role');

    // Seed a default theme if none exists yet — gives the picker a
    // sensible starting color when the shopper opens "Add my own colors"
    // without first tapping a preset swatch.
    var state = readState();
    if (!state.bannerTheme) {
      state.bannerTheme = {
        code:           '',
        primaryColor:   DEFAULT_CUSTOM_HEX,
        secondaryColor: DEFAULT_CUSTOM_HEX,
        tertiaryColor:  DEFAULT_CUSTOM_HEX,
        accentColor:    DEFAULT_CUSTOM_HEX,
      };
      updateChips(state.bannerTheme);
    }

    syncPickerFromState();
    root.hidden = false;

    // Two-frame wait: first frame mounts the DOM with hidden:false (so
    // getBoundingClientRect returns real numbers), second frame lays it
    // out at the right anchor.
    requestAnimationFrame(function () { positionPicker(); });

    bindDismissHandlers();
  }

  function closeColorPicker() {
    if (!picker.root || picker.root.hidden) return;
    commitTheme();
    picker.root.hidden = true;
    if (picker.activeChip) picker.activeChip.classList.remove('is-active');
    picker.activeChip = null;
    picker.activeRole = null;
    unbindDismissHandlers();
  }

  function bindDismissHandlers() {
    if (picker.dismissHandler) return;
    picker.dismissHandler = function (e) {
      var t = e.target;
      if (!(t instanceof Element)) { closeColorPicker(); return; }
      // Keep the picker open when the shopper taps inside it or taps
      // another chip (tap-through opens the picker on the new role
      // without flickering).
      if (picker.root.contains(t) || t.closest('.qc-chip')) return;
      closeColorPicker();
    };
    document.addEventListener('pointerdown', picker.dismissHandler, true);

    picker.keyHandler = function (e) {
      if (e.key === 'Escape') { e.preventDefault(); closeColorPicker(); }
    };
    window.addEventListener('keydown', picker.keyHandler);

    picker.reflowHandler = function () { positionPicker(); };
    window.addEventListener('resize', picker.reflowHandler);
    window.addEventListener('scroll', picker.reflowHandler, { passive: true });
  }

  function unbindDismissHandlers() {
    if (picker.dismissHandler) {
      document.removeEventListener('pointerdown', picker.dismissHandler, true);
      picker.dismissHandler = null;
    }
    if (picker.keyHandler) {
      window.removeEventListener('keydown', picker.keyHandler);
      picker.keyHandler = null;
    }
    if (picker.reflowHandler) {
      window.removeEventListener('resize', picker.reflowHandler);
      window.removeEventListener('scroll', picker.reflowHandler);
      picker.reflowHandler = null;
    }
  }

  function bindChipClicks() {
    var chips = document.getElementById('qc-chips');
    if (!chips || chips._qcChipBound) return;
    chips._qcChipBound = true;

    chips.addEventListener('click', function (e) {
      var chip = e.target.closest('.qc-chip');
      if (!chip || !chips.contains(chip)) return;
      openColorPicker(chip);
    });

    // Keyboard a11y: chips are <div>s in the markup, so add the
    // role/tabindex/keydown affordances ourselves.
    var chipEls = chips.querySelectorAll('.qc-chip');
    for (var i = 0; i < chipEls.length; i++) {
      var c = chipEls[i];
      if (!c.hasAttribute('tabindex')) c.setAttribute('tabindex', '0');
      if (!c.hasAttribute('role'))     c.setAttribute('role', 'button');
      c.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openColorPicker(e.currentTarget);
        }
      });
    }
  }

  // ─── Selected-color pill (above the product grid) ─────────────────────────
  // Mirrors the Style filter pills: when a color theme is active we show a
  // chip above the grid carrying a mini two-tone "Color Set" swatch (Figma
  // 9806:292598) + an ✕. Clicking it clears the selection (same revert-to-
  // per-card-colors path as the colorful swatch). Lives in its own container
  // (#collection-color-pill) so collection-style-filters.js can keep owning
  // the adjacent style-pill container without either wiping the other.
  function renderColorPill(theme) {
    var el = document.getElementById('collection-color-pill');
    if (!el) return;
    var primary   = (theme && theme.primaryColor)   || '#ccc';
    var secondary = (theme && theme.secondaryColor) || (theme && theme.primaryColor) || '#ccc';
    el.innerHTML =
      '<button type="button" class="color-filter-pill" data-remove-color="1" aria-label="Remove selected color theme">' +
        '<span class="color-filter-pill__swatch" aria-hidden="true">' +
          '<span class="color-filter-pill__row color-filter-pill__row--top" style="background-color:' + escapeAttr(primary) + ';"></span>' +
          '<span class="color-filter-pill__row color-filter-pill__row--bottom" style="background-color:' + escapeAttr(secondary) + ';"></span>' +
        '</span>' +
        '<svg class="style-filter-pill__remove" aria-hidden="true" focusable="false" viewBox="0 0 10 10" width="10" height="10"><path fill="currentColor" d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.2"/></svg>' +
      '</button>';
    el.hidden = false;
    var btn = el.querySelector('[data-remove-color]');
    if (btn) btn.addEventListener('click', unselectColorTheme);
  }

  function clearColorPill() {
    var el = document.getElementById('collection-color-pill');
    if (!el) return;
    el.innerHTML = '';
    el.hidden = true;
  }

  // Tears down a card's design preview so the ORIGINAL Shopify product
  // image shows again: removes the injected `.card-design-preview` overlay
  // (which also carries the team-name / logo overlays rendered into its
  // SVG) and un-hides the product media the overlay had covered.
  function restoreOriginalCard(card) {
    var mediaWrap = card.querySelector('.card-product-figma__image-frame .media--transparent');
    if (mediaWrap) mediaWrap.style.visibility = '';
    var previewEl = card.querySelector('.card-design-preview');
    if (previewEl && previewEl.parentNode) previewEl.parentNode.removeChild(previewEl);
  }

  function restoreAllCards() {
    var cards = document.querySelectorAll('.card-wrapper[data-sku]');
    for (var i = 0; i < cards.length; i++) restoreOriginalCard(cards[i]);
  }

  // Clears the active color theme and restores every card to its original
  // Shopify product image. Wired to the color pill's ✕.
  //
  // A color pick auto-promotes the API default font (selectDefaultFont),
  // so clearing only bannerTheme would leave the font "intent" set and the
  // auto-loader (team-design.text-auto.js) would just repaint the jersey.
  // We therefore clear the color-driven intents too, then hand off to the
  // auto module's clearAll(), which restores the underlying product media
  // for cards with no remaining intent while preserving any still-active
  // team-text / logo overlay. Fallback strips the previews directly if the
  // auto module isn't present.
  function unselectColorTheme() {
    var state = readState();
    state.bannerTheme          = null;
    state.bannerTextFont       = null;
    state.bannerNumberFont     = null;
    state.bannerPlayerNameFont = null;

    var container = document.getElementById('qc-swatches');
    if (container) {
      var actives = container.querySelectorAll('.qc-swatch.is-active');
      for (var i = 0; i < actives.length; i++) actives[i].classList.remove('is-active');
    }

    var chipsEl = document.getElementById('qc-chips');
    if (chipsEl) chipsEl.hidden = true;
    var chipsLauncher = document.getElementById('qc-chips-launcher');
    if (chipsLauncher) {
      chipsLauncher.hidden = false;
      chipsLauncher.setAttribute('aria-expanded', 'false');
    }

    var ns = window.CollectionTeamText;
    if (ns && ns.auto && typeof ns.auto.clearAll === 'function') {
      ns.auto.clearAll();
    } else {
      restoreAllCards();
    }
    clearColorPill();
  }

  // ─── Bind swatch clicks ──────────────────────────────────────────────────
  function bindSwatchClicks(container) {
    container.addEventListener('click', function (e) {
      var sw = e.target.closest('.qc-swatch');
      if (!sw || !container.contains(sw)) return;

      var state = readState();

      // Color pick is one of the four "arm" interactions — flip the auto-
      // load gate so cookie-restored team text / logo can paint cards too.
      var nsArm = window.CollectionTeamText && window.CollectionTeamText.arm;
      if (nsArm) nsArm();

      // Colorful swatch — treat as an explicit intent signal that paints
      // every card with its OWN first-swatch theme (the per-card jersey
      // colors). Clears state.bannerTheme so text-auto.js /
      // text-render.js / logo.js (all of which fall back to the card's
      // first swatch when bannerTheme is null) resume per-card behavior
      // for future renders, and force-loads SVGs on every card via
      // applyPerCardThemes so the click is visible even on a fresh page
      // with no prior intent. We also hide the C1..C4 chip strip —
      // those chips reflect the banner theme and have nothing to display
      // in per-card mode — and re-show the mobile chips launcher so the
      // shopper can re-opt-in to custom roles after picking a real theme.
      // Promotes the default font on the same intent-click pattern the
      // real-theme branch below uses.
      if (sw.hasAttribute('data-color-full')) {
        state.bannerTheme = null;
        setActiveSwatch(container, sw);

        var chipsEl = document.getElementById('qc-chips');
        if (chipsEl) chipsEl.hidden = true;
        var chipsLauncher = document.getElementById('qc-chips-launcher');
        if (chipsLauncher) {
          chipsLauncher.hidden = false;
          chipsLauncher.setAttribute('aria-expanded', 'false');
        }

        applyPerCardThemes();
        clearColorPill();
        selectDefaultFont();
        return;
      }

      var theme = {
        code:           sw.getAttribute('data-theme-code') || '',
        primaryColor:   sw.getAttribute('data-primary')    || '',
        secondaryColor: sw.getAttribute('data-secondary')  || '',
        tertiaryColor:  sw.getAttribute('data-tertiary')   || '',
        accentColor:    sw.getAttribute('data-accent')     || '',
      };

      state.bannerTheme = theme;

      setActiveSwatch(container, sw);

      // C1..C4 chip strip is hidden by default (the section liquid sets
      // the `hidden` attribute) — reveal it the first time a color is
      // picked. Cheap to call every click since the attribute removal is
      // idempotent. On mobile the chips stay visually suppressed by the
      // launcher sibling rule until the shopper taps "+ Add my own
      // colors", but the underlying `updateChips(theme)` paint still
      // runs so the strip is ready the moment the launcher is dismissed.
      var chipsEl = document.getElementById('qc-chips');
      if (chipsEl) chipsEl.hidden = false;
      updateChips(theme);

      applyThemeToAllCards(theme);
      renderColorPill(theme);

      // The shopper just opted into the customization flow by picking a
      // color; auto-promote the API default font to active so the team-
      // text overlay (which applyThemeToAllCards is about to trigger via
      // the MutationObserver in text.js) renders in the configured font
      // rather than the Anton fallback. No-op if they already picked a
      // font earlier.
      selectDefaultFont();
    });
  }

  // ─── Fonts ───────────────────────────────────────────────────────────────
  // A font's `fontUrl` from the API can be one of two things:
  //   - `isUploaded=false` (default): a stylesheet endpoint (Google Fonts CSS,
  //     fonts.com CSS, etc.) whose CSS declares its own `@font-face` blocks
  //     pointing at the actual glyph files. Loaded via `<link rel="stylesheet">`.
  //   - `isUploaded=true`: an admin-uploaded raw font binary (`.ttf|.otf|
  //     .woff|.woff2`) at `DESIGN_TOOL/FONTS/…`. There's no CSS wrapper, so we
  //     have to synthesize an `@font-face { src: url(...) }` rule ourselves.
  // Both cases are idempotent — repeat calls for the same URL are ignored.
  var RAW_FONT_EXTS = ['ttf', 'otf', 'woff', 'woff2'];
  function isRawFontFileUrl(url) {
    if (!url) return false;
    var withoutQuery = url.split(/[?#]/)[0];
    var parts = withoutQuery.split('.');
    var ext = (parts[parts.length - 1] || '').toLowerCase();
    return RAW_FONT_EXTS.indexOf(ext) !== -1;
  }
  // Escape backslash, `"`, and newlines so admin-entered family/url can't
  // terminate the `"…"` CSS string literal and inject extra declarations.
  function escapeCssStringLiteral(s) {
    return String(s == null ? '' : s)
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/[\r\n]/g, '');
  }
  // In-memory dedupe of URLs we've already asked the browser to load. The
  // previous approach — `querySelector('link[data-qc-font-url="…"]')` —
  // threw a SyntaxError on some Google-Fonts CSS URLs (a stray character
  // in the query string that the CSS attribute-selector parser rejected),
  // which killed the whole font-section setup because the error bubbled
  // out of injectFontFaces() before renderFonts() could run.
  var loadedFontUrls = new Set();
  function loadFontStylesheet(url) {
    if (!url || loadedFontUrls.has(url)) return;
    loadedFontUrls.add(url);
    var link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = url;
    link.setAttribute('data-qc-font-url', url);
    document.head.appendChild(link);
  }
  function injectRawFontFace(family, url) {
    if (!family || !url || loadedFontUrls.has(url)) return;
    loadedFontUrls.add(url);
    var style = document.createElement('style');
    style.setAttribute('data-qc-font-url', url);
    style.textContent =
      '@font-face { font-family: "' + escapeCssStringLiteral(family) +
      '"; src: url("' + escapeCssStringLiteral(url) +
      '"); font-display: swap; }';
    document.head.appendChild(style);
  }
  // Loads one font (either flavor). Falls back to stylesheet loading when
  // `isUploaded` is missing but the URL's extension is a raw font file, so
  // an older API row without the flag still renders correctly.
  function loadFontForFont(font) {
    if (!font || !font.fontUrl) return;
    try {
      var uploaded = font.isUploaded === true || isRawFontFileUrl(font.fontUrl);
      if (uploaded) {
        injectRawFontFace(font.family, font.fontUrl);
      } else {
        loadFontStylesheet(font.fontUrl);
      }
    } catch (e) {
      // Never let one bad row take down the whole picker.
    }
  }

  // Each pair carries THREE fonts (textFont + numberFont + playerNameFont)
  // which may or may not be the same — we preload all of them
  // unconditionally so the "A9" preview tile and the SVG team / name /
  // number text never paint the fallback for an unloaded family.
  function injectFontFaces(fontPairs) {
    for (var i = 0; i < fontPairs.length; i++) {
      var p = fontPairs[i];
      if (!p) continue;
      loadFontForFont(p.textFont);
      loadFontForFont(p.numberFont);
      loadFontForFont(p.playerNameFont);
    }
  }

  // Resolves once the browser has actually fetched + parsed the font face
  // for `family`. The size in the spec string is irrelevant for loading —
  // we just need any valid `<font-shorthand>`-compatible string. Used to
  // gate SVG re-renders so getComputedTextLength() inside text-render.js
  // measures against the real glyph metrics instead of the fallback font
  // (which would give a wrong shrink-to-fit size).
  function ensureFontReady(family) {
    if (!family) return Promise.resolve();
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    try {
      return document.fonts.load('16px "' + family + '"').catch(function () { return null; });
    } catch (e) {
      return Promise.resolve();
    }
  }

  function refreshCardPreviews() {
    var inject = window.CollectionTeamText && window.CollectionTeamText.inject;
    if (inject && inject.refreshAllVisiblePreviews) {
      inject.refreshAllVisiblePreviews();
    }
  }

  // Cached list of the pairs currently rendered, in the same order as the
  // buttons. Casing (`allCaps`) and suffix now live on each font's `fontConfig`
  // (populated in the dashboard's Add Font modal, keyed to `isUploaded`
  // custom uploads) so the click handler needs the full font object, not
  // just its id/family — it looks the object up by pair index here rather
  // than round-tripping serialized JSON through data-attributes.
  var currentFontPairs = [];
  // Fallback for `basePx` when we can't measure — matches the
  // `font-size: 22px` in the .qc-font__btn CSS (both desktop and mobile
  // snippets set 22px).
  var QC_FONT_TILE_DEFAULT_FONT_PX = 22;
  function renderFonts(container, fontPairs) {
    // Drop any previously appended buttons but keep the static label.
    var oldBtns = container.querySelectorAll('.qc-font__btn');
    for (var i = 0; i < oldBtns.length; i++) {
      oldBtns[i].parentNode.removeChild(oldBtns[i]);
    }

    currentFontPairs = fontPairs.slice();

    // Measure the tile's CSS font-size once per render so we can scale in
    // absolute pixels (basePx * sizeMultiplier) instead of `em`. Using
    // pixels gives a deterministic result even if a parent's font-size
    // ever changes independently of the tile's CSS.
    var basePx = QC_FONT_TILE_DEFAULT_FONT_PX;
    try {
      var probe = document.createElement('button');
      probe.className = 'qc-font__btn';
      probe.style.visibility = 'hidden';
      probe.style.position = 'absolute';
      container.appendChild(probe);
      var measured = parseFloat(getComputedStyle(probe).fontSize);
      if (measured > 0) basePx = measured;
      container.removeChild(probe);
    } catch (e) {
      // Fall back to the constant.
    }

    fontPairs.forEach(function (p, idx) {
      var textFont   = (p && p.textFont)   || {};
      var numberFont = (p && p.numberFont) || {};
      // Fall back to the text side when the payload predates the player
      // slot (mirrors the backend's `playerNameFontId ?? textFontId`).
      var playerFont = (p && (p.playerNameFont || p.textFont)) || {};

      var btn = document.createElement('button');
      btn.type      = 'button';
      // No `.is-active` on render — picker starts neutral. `selectDefaultFont`
      // (called on first shopper interaction) or `bindFontClicks` promote
      // a button to active.
      btn.className = 'qc-font__btn';
      btn.setAttribute('data-pair-index',         String(idx));
      btn.setAttribute('data-text-font-id',       textFont.id     || '');
      btn.setAttribute('data-text-font-family',   textFont.family || '');
      btn.setAttribute('data-number-font-id',     numberFont.id     || '');
      btn.setAttribute('data-number-font-family', numberFont.family || '');
      btn.setAttribute('data-player-font-id',     playerFont.id     || '');
      btn.setAttribute('data-player-font-family', playerFont.family || '');
      btn.setAttribute(
        'aria-label',
        (textFont.family   || 'Font') + ' / ' + (numberFont.family || 'Font'),
      );

      // Fixed "A9" preview across all buttons, but each glyph is rendered
      // in its own half of the pair — `A` in textFont (used for team /
      // name slots in the SVG), `9` in numberFont (used for the number
      // slot). The API's `previewText` field is ignored on purpose.
      // `fontConfig.sizeMultiplier` — set per-font in the dashboard for
      // decorative faces that "read small" at the same nominal size —
      // scales each glyph so the picker matches what actually paints on
      // the jersey.
      var textCfg   = (textFont   && textFont.fontConfig)   || {};
      var numberCfg = (numberFont && numberFont.fontConfig) || {};
      var textScale =
        typeof textCfg.sizeMultiplier === 'number' && textCfg.sizeMultiplier > 0
          ? textCfg.sizeMultiplier : 1;
      var numberScale =
        typeof numberCfg.sizeMultiplier === 'number' && numberCfg.sizeMultiplier > 0
          ? numberCfg.sizeMultiplier : 1;

      // Same trick as the team-name input: for scaled decorative fonts,
      // fall back to the font's own line-height metric and let strokes
      // that live outside the em-box render past the span's bounding box
      // (`line-height: normal` + `overflow: visible`) so the tile shows
      // the full glyph without the top-padding + bottom-clip issue.
      // Plus a `translateY` nudge (mirrors the squad calculator's
      // picker in libs/frontend/calculator/src/calculator.squad.ts):
      // decorative fonts (Retrock, ExpoPrint) sit LOW in their em-box,
      // so with the button's `align-items: center` layout the visible
      // letter drops well below the number span. Font-size and the
      // shift are computed as absolute pixels (`basePx * sizeMultiplier`)
      // so the result is deterministic — `em`-based sizing depended on
      // the inherited context which the caller couldn't observe.
      var letterSpan = document.createElement('span');
      letterSpan.className = 'qc-font__btn-letter';
      letterSpan.style.fontFamily =
        "'" + String(textFont.family || '').replace(/'/g, '') + "', sans-serif";
      if (textScale !== 1) {
        letterSpan.style.fontSize        = (basePx * textScale) + 'px';
        letterSpan.style.lineHeight      = 'normal';
        letterSpan.style.overflow        = 'visible';
        letterSpan.style.transform       =
          'translateY(-' + ((textScale) * 0.2 * basePx) + 'px)';
        letterSpan.style.transformOrigin = 'center';
      }
      letterSpan.textContent = 'A';

      var numberSpan = document.createElement('span');
      numberSpan.className = 'qc-font__btn-number';
      numberSpan.style.fontFamily =
        "'" + String(numberFont.family || '').replace(/'/g, '') + "', sans-serif";
      if (numberScale !== 1) {
        numberSpan.style.fontSize        = (basePx * numberScale) + 'px';
        numberSpan.style.lineHeight      = 'normal';
        numberSpan.style.overflow        = 'visible';
        numberSpan.style.transform       =
          'translateY(-' + ((numberScale) * 0.2 * basePx) + 'px)';
        numberSpan.style.transformOrigin = 'center';
      }
      numberSpan.textContent = '9';

      btn.appendChild(letterSpan);
      btn.appendChild(numberSpan);
      container.appendChild(btn);
    });
  }

  function setActiveFont(container, btn) {
    var all = container.querySelectorAll('.qc-font__btn');
    for (var i = 0; i < all.length; i++) {
      all[i].classList.toggle('is-active', all[i] === btn);
    }
  }

  // Defer `cb` until at least one `.card-design-preview` actually has an
  // inline <svg> child (a real "card painted" signal, not just a fetch
  // kicked off). Fires immediately when a preview already carries an SVG
  // — common on the second-plus arm interaction within a single visit.
  // Used to keep picker active-state changes (colorful swatch highlight,
  // default-font highlight) from running ahead of the visible result on
  // the cards: focusing the team input would otherwise pop the picker
  // marks on while the cards were still showing product images.
  function whenSvgRenderedOnAnyCard(cb) {
    if (document.querySelector('.card-design-preview > svg')) { cb(); return; }
    var root = document.querySelector('.product-grid-container') ||
               document.getElementById('product-grid') ||
               document.body;
    if (!root) return;
    var observer = new MutationObserver(function () {
      if (document.querySelector('.card-design-preview > svg')) {
        observer.disconnect();
        cb();
      }
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  // Mirrors the SVG side: the team-name textarea picks up the active text
  // font's family AND its `fontConfig.allCaps` rule. (`fontConfig.suffix`
  // is intentionally NOT echoed in the input — it's only appended at
  // render time so the shopper sees the literal text they typed plus a
  // visual suffix on the jersey preview.)
  //
  // The casing rule is applied purely via CSS `text-transform` — we do
  // NOT mutate `input.value`. Mutating the value would be irreversible
  // when the shopper switches from a caps-on font to a caps-off font,
  // leaving their original mixed-case text lost. With CSS the value is
  // always the literal text the shopper typed, and toggling caps just
  // flips the visual transform. The SVG overlay handles its own
  // uppercase pass in text-render.js (`applyTextOptions`), so the
  // jersey preview still reflects the active font's casing rule
  // regardless of the value stored in the input.

  function applyTextOptionsToInput() {
    var input = document.getElementById('collection-team-name-input');
    if (!input) return;
    var state = readState();
    var textFont = state.bannerTextFont || null;
    var family = textFont && textFont.family;
    // `fontConfig` lives on each font (set per-font in the dashboard "Add
    // Font" modal). No pair-level override anymore. `suffix` is applied
    // at SVG-render time only — the input intentionally never echoes it.
    var cfg    = (textFont && textFont.fontConfig) || {};
    var caps   = !!cfg.allCaps;
    var scale  = typeof cfg.sizeMultiplier === 'number' && cfg.sizeMultiplier > 0
      ? cfg.sizeMultiplier
      : 1;

    var familyStack = family
      ? "'" + String(family).replace(/'/g, '') + "', 'Anton', 'Barlow Semi Condensed', 'Barlow', sans-serif"
      : '';

    if (familyStack) {
      input.style.fontFamily = familyStack;
    } else {
      input.style.fontFamily = '';
    }

    input.style.textTransform = caps ? 'uppercase' : '';
    // Scale in absolute pixels — measure the input's own CSS font-size
    // (with any prior inline fontSize temporarily cleared) so we get the
    // stylesheet-defined base, then multiply. Anchoring to px makes the
    // result deterministic; the earlier `em`-based scaling chained
    // against inherited context and could produce surprising sizes.
    if (scale !== 1) {
      var prevInputFontSize = input.style.fontSize;
      input.style.fontSize = '';
      var inputBasePx = parseFloat(getComputedStyle(input).fontSize);
      if (!inputBasePx || isNaN(inputBasePx)) {
        // Restore whatever was there and skip the resize when we can't
        // measure (fires only in detached / display: none contexts).
        input.style.fontSize = prevInputFontSize;
      } else {
        input.style.fontSize = (inputBasePx * scale) + 'px';
      }
    } else {
      input.style.fontSize = '';
    }
    // Decorative fonts (Retrock, ExpoPrint) have swashes/underlines that
    // extend well beyond the standard em-box. Two knobs are needed:
    //   1. `line-height: normal` — the browser derives this from the
    //      font's own OS/2 metrics (which for these faces already include
    //      the extra descender room), replacing the theme's fixed 1.35
    //      that was leaving a big empty band above the glyphs.
    //   2. `overflow: visible` — the input's stylesheet sets
    //      `overflow: hidden`, so ascenders/underline strokes that live
    //      outside the em-box get clipped by the input's bounding box.
    //      Turn clipping off while a scaled font is active so the shopper
    //      sees the full glyph. Reset both at 1× so normal sans-serif
    //      fonts keep the theme's original spacing + clipping.
    input.style.lineHeight = scale !== 1 ? 'normal' : '';
    input.style.overflow   = scale !== 1 ? 'visible' : '';

    // Re-run the autosize that text.js did at init: the previous
    // scrollHeight was measured against the OLD (unloaded) font metrics,
    // so the input's `height` is now shorter than the glyphs actually
    // need — that's what squashes the placeholder into a tiny sliver on
    // first render. Do it once immediately (so we're at least close to
    // right) and again after the family finishes fetching so glyph
    // metrics are accurate.
    function resizeInputToContent() {
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
    }
    resizeInputToContent();
    ensureFontReady(family).then(resizeInputToContent);
  }

  // Mirrors selectDefaultFont() for the colorful swatch: marks it active
  // only once SVGs are actually rendered on the cards (the trigger sites
  // that call this also drive an SVG render). A no-op if a real theme is
  // already picked — in that case the picked theme owns the active state
  // and the colorful swatch must stay deselected.
  function markColorfulActiveIfDefault() {
    var state = readState();
    if (state.bannerTheme) return;
    var container = document.getElementById('qc-swatches');
    if (!container) return;
    var colorful = container.querySelector('.qc-swatch--colorful');
    if (!colorful) return;
    if (colorful.classList.contains('is-active')) return;
    setActiveSwatch(container, colorful);
  }

  // Promotes the API-declared default font pair to active. Called when
  // the shopper does their first "uses-the-font" action — typing a team
  // name, or picking a color in the banner. A no-op if:
  //   - they've already picked a pair (either side of the pair is set)
  //   - the API didn't seed any pairs for this collection
  // Exposed on window.CollectionQuickCustomization so text.js can call
  // it from its input handler.
  function selectDefaultFont() {
    // Picker active-state changes (colorful swatch, default-pair tile)
    // are queued behind whenSvgRenderedOnAnyCard so a focus-arms-only
    // interaction doesn't pop them on while the cards still show their
    // Shopify product image. The state mutations below
    // (bannerTextFont / bannerNumberFont — including their `fontConfig`
    // and `isUploaded` fields) still run synchronously so renderers
    // downstream see the right intent — only the visible "this is
    // selected" marks wait.
    whenSvgRenderedOnAnyCard(markColorfulActiveIfDefault);

    var state = readState();
    if (state.bannerTextFont || state.bannerNumberFont) return;
    if (!apiDefaultFontPair) return;

    var tf = apiDefaultFontPair.textFont       || {};
    var nf = apiDefaultFontPair.numberFont     || {};
    // Player-name slot font. Older cached payloads may not carry it —
    // fall back to the text font, matching the backend's own default
    // (`playerNameFontId ?? textFontId`).
    var pf = apiDefaultFontPair.playerNameFont || apiDefaultFontPair.textFont || {};
    // Carry `fontConfig` (suffix / sizeMultiplier / allCaps) and `isUploaded`
    // through so downstream readers (text-render, input CSS) can pull the
    // rendering options straight off the active text font instead of
    // relying on now-removed pair-level fields.
    state.bannerTextFont   = {
      id:         tf.id     || '',
      family:     tf.family || '',
      fontUrl:    tf.fontUrl || '',
      fontConfig: tf.fontConfig || null,
      isUploaded: tf.isUploaded === true,
    };
    state.bannerNumberFont = {
      id:         nf.id     || '',
      family:     nf.family || '',
      fontUrl:    nf.fontUrl || '',
      fontConfig: nf.fontConfig || null,
      isUploaded: nf.isUploaded === true,
    };
    state.bannerPlayerNameFont = {
      id:         pf.id     || '',
      family:     pf.family || '',
      fontUrl:    pf.fontUrl || '',
      fontConfig: pf.fontConfig || null,
      isUploaded: pf.isUploaded === true,
    };

    var fontsEl = document.getElementById('qc-fonts');
    if (fontsEl) {
      // Match a button by the combined (textFontId, numberFontId) key —
      // a single font id is no longer unique because the same font can
      // appear as the text or number side of different pairs. Defer the
      // visual highlight via whenSvgRenderedOnAnyCard so the default
      // pair only lights up once the cards actually repaint.
      var btn = fontsEl.querySelector(
        '[data-text-font-id="' + (tf.id || '') + '"]' +
        '[data-number-font-id="' + (nf.id || '') + '"]'
      );
      if (btn) {
        var fontBtn   = btn;
        var fontsCtnr = fontsEl;
        whenSvgRenderedOnAnyCard(function () { setActiveFont(fontsCtnr, fontBtn); });
      }
    }

    applyTextOptionsToInput();

    // Wait for ALL the pair's font files before triggering renders so getComputedText-
    // Length in text-render.js measures against real glyph metrics for
    // each slot's typeface. We call BOTH auto.renderVisible (loads SVGs
    // on cards that don't yet have one — the font-only case) and
    // refreshCardPreviews (re-injects the team-text / number / name
    // overlays on cards whose SVG was already loaded).
    Promise.all([
      ensureFontReady(tf.family),
      ensureFontReady(nf.family),
      ensureFontReady(pf.family),
    ]).then(function () {
      var auto = window.CollectionTeamText && window.CollectionTeamText.auto;
      if (auto && auto.renderVisible) auto.renderVisible();
      refreshCardPreviews();
    });
  }

  function bindFontClicks(container) {
    container.addEventListener('click', function (e) {
      var btn = e.target.closest('.qc-font__btn');
      if (!btn || !container.contains(btn)) return;

      // Rendering options (allCaps / suffix / sizeMultiplier) now live on
      // each font's `fontConfig` in the dashboard, so we need the FULL
      // font objects from the API, not just id/family. Look them up in
      // `currentFontPairs` by pair index (stashed at render time). Fall
      // back to id/family from the button attrs if the index is stale.
      var idx  = parseInt(btn.getAttribute('data-pair-index') || '-1', 10);
      var pair = (idx >= 0 && idx < currentFontPairs.length) ? currentFontPairs[idx] : null;
      var tf   = (pair && pair.textFont)   || {};
      var nf   = (pair && pair.numberFont) || {};
      // Player-name slot — falls back to the text side when the payload
      // predates the field (mirrors the backend default).
      var pf   = (pair && (pair.playerNameFont || pair.textFont)) || {};

      var textFont = {
        id:         tf.id     || btn.getAttribute('data-text-font-id')     || '',
        family:     tf.family || btn.getAttribute('data-text-font-family') || '',
        fontUrl:    tf.fontUrl || '',
        fontConfig: tf.fontConfig || null,
        isUploaded: tf.isUploaded === true,
      };
      var numberFont = {
        id:         nf.id     || btn.getAttribute('data-number-font-id')     || '',
        family:     nf.family || btn.getAttribute('data-number-font-family') || '',
        fontUrl:    nf.fontUrl || '',
        fontConfig: nf.fontConfig || null,
        isUploaded: nf.isUploaded === true,
      };
      var playerNameFont = {
        id:         pf.id     || btn.getAttribute('data-player-font-id')     || '',
        family:     pf.family || btn.getAttribute('data-player-font-family') || '',
        fontUrl:    pf.fontUrl || '',
        fontConfig: pf.fontConfig || null,
        isUploaded: pf.isUploaded === true,
      };

      var state = readState();

      // Persist the picked pair BEFORE arming. arm() calls selectDefaultFont(),
      // which short-circuits on `state.bannerTextFont || state.bannerNumberFont`.
      // Reversing this order lets selectDefaultFont see the (still null) state,
      // write the API default pair into those fields, and queue a deferred
      // setActiveFont on the default button behind whenSvgRenderedOnAnyCard —
      // that callback then fires once SVGs paint and silently switches the
      // picker selection from the shopper's pick to the first/default pair.
      state.bannerTextFont       = textFont;
      state.bannerNumberFont     = numberFont;
      state.bannerPlayerNameFont = playerNameFont;

      // Font pick is one of the four "arm" interactions — flip the auto-
      // load gate so cookie-restored team text / logo can paint cards too.
      var nsArm = window.CollectionTeamText && window.CollectionTeamText.arm;
      if (nsArm) nsArm();

      setActiveFont(container, btn);
      applyTextOptionsToInput();

      // Same SVGs-just-loaded signal selectDefaultFont() uses — promote
      // the colorful swatch when no real theme is picked, so the swatch
      // grid reflects the actual rendered colors (per-card defaults).
      // Deferred via whenSvgRenderedOnAnyCard so the swatch highlight
      // matches the moment the cards actually repaint, not the click.
      whenSvgRenderedOnAnyCard(markColorfulActiveIfDefault);

      // Picking a font is the shopper's "I want to customize" signal, even
      // before any team name is typed. Trigger an SVG auto-load on every
      // visible card (same path as typing a team name) so they see their
      // jerseys repaint with per-card colors + the "TEAM NAME" placeholder
      // in the picked font. If SVGs were already loaded (color picked or
      // text typed earlier), refreshCardPreviews repaints just the text.
      Promise.all([
        ensureFontReady(textFont.family),
        ensureFontReady(numberFont.family),
        ensureFontReady(playerNameFont.family),
      ]).then(function () {
        var auto = window.CollectionTeamText && window.CollectionTeamText.auto;
        if (auto && auto.renderVisible) auto.renderVisible();
        refreshCardPreviews();
      });
    });
  }

  // ─── Relocate sidebar into the grid section ─────────────────────────────
  // The banner lives in its own Shopify section above the grid section, so
  // by default the sidebar renders ABOVE the grid (and would overlap the
  // hero banner if we used position: fixed). Move it into
  // .collection-grid-section at runtime so position: sticky works within
  // the grid's height — the sidebar sits next to the products and unsticks
  // naturally when the grid scrolls past, without ever overlapping the
  // hero above.
  //
  // The sidebar wraps BOTH the Quick Customization panel and the Create-Once
  // cross-product callout, so reparenting one element moves the full stack.
  function getSidebar() {
    return document.querySelector('[class^="qc-sidebar-"], [class*=" qc-sidebar-"]');
  }

  function relocatePanelIntoGrid() {
    var sidebar     = getSidebar();
    var gridSection = document.querySelector('.collection-grid-section');
    if (!sidebar || !gridSection) return;
    // Idempotent — already arranged (sidebar is in the grid section and
    // the content wrap exists), so nothing to do.
    if (
      gridSection.contains(sidebar) &&
      gridSection.querySelector(':scope > .collection-grid-section__content') &&
      gridSection.classList.contains('has-qc-panel')
    ) return;

    // If the sidebar still lives outside the grid section (legacy
    // section-based flow), pull it in. The new flow renders it as an
    // inline child via the snippet, so this branch is a no-op there.
    if (!gridSection.contains(sidebar)) {
      gridSection.insertBefore(sidebar, gridSection.firstChild);
    }

    // Wrap every non-sidebar child into .collection-grid-section__content
    // so the CSS Grid layout has exactly two columns to fill (sidebar +
    // content). Skipped if the wrap already exists.
    var contentWrap = gridSection.querySelector(':scope > .collection-grid-section__content');
    if (!contentWrap) {
      contentWrap = document.createElement('div');
      contentWrap.className = 'collection-grid-section__content';
      var kids = Array.prototype.slice.call(gridSection.children);
      for (var i = 0; i < kids.length; i++) {
        if (kids[i] === sidebar) continue;
        contentWrap.appendChild(kids[i]);
      }
      gridSection.appendChild(contentWrap);
    }

    // Make sure the sidebar is the FIRST grid child (column 1 of the
    // template). Reparenting can shuffle order, so re-pin if needed.
    if (gridSection.firstElementChild !== sidebar) {
      gridSection.insertBefore(sidebar, gridSection.firstChild);
    }

    gridSection.classList.add('has-qc-panel');

    // text.js runs an autosize on the team-name <textarea> at init:
    //   input.style.height = input.scrollHeight + 'px'
    // …but at that point the sidebar is still inside a display:none section
    // wrapper, so scrollHeight is 0 — the textarea gets a stuck inline
    // `height: 0px` and becomes invisible / unclickable even after we
    // reparent the sidebar into the visible grid section. Clearing the
    // inline height restores natural sizing (rows="1") and lets the next
    // input event re-run autosize against the now-visible element.
    var nameInput = document.getElementById('collection-team-name-input');
    if (nameInput) {
      nameInput.style.height = '';
    }
  }

  // ─── Eager relocation ────────────────────────────────────────────────────
  // text.js is *earlier* in the defer chain and calls autosize() on the
  // team-name textarea during its own init — which runs synchronously at
  // script execution. If the sidebar is still inside the display:none section
  // wrapper at that moment, scrollHeight is 0 and the textarea gets stuck
  // with an inline `height: 0px`. We relocate the sidebar *now* (before any
  // later defer script runs) so text.js's autosize measures against a
  // visible element and produces the correct height.
  relocatePanelIntoGrid();

  // Hard-remove the sidebar + restore the grid section to its non-panel
  // layout (drop has-qc-panel so the side-by-side flex rules stop applying
  // and the grid reclaims the full width).
  function hidePanel() {
    var sidebar = getSidebar();
    if (sidebar && sidebar.parentNode) sidebar.parentNode.removeChild(sidebar);
    var gridSection = document.querySelector('.collection-grid-section.has-qc-panel');
    if (gridSection) gridSection.classList.remove('has-qc-panel');
    var launcher = document.querySelector('[class*="qc-launcher-"]');
    if (launcher && launcher.parentNode) launcher.parentNode.removeChild(launcher);
  }

  // ─── Mobile UI: tab nav + bottom launcher ────────────────────────────────
  // Below 880px the sidebar collapses into a fixed-position pill at the
  // bottom of the viewport. Tapping the pill expands a tabbed overlay
  // with the same controls (color / text / logo / font).
  //
  // The launcher itself is hidden until the products grid scrolls into
  // view, then unhidden — and re-hidden when the grid scrolls past — so
  // it doesn't float over unrelated page sections (hero banner, FAQ,
  // testimonials etc.). Driven by an IntersectionObserver below.
  function bindTabClicks() {
    var tabs = document.querySelectorAll('.qc-tab');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var name = tab.getAttribute('data-qc-tab');
        if (!name) return;
        // Activate this tab + its matching pane; deactivate siblings.
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        document.querySelectorAll('.qc-pane').forEach(function (pane) {
          pane.classList.toggle('is-active', pane.getAttribute('data-qc-pane') === name);
        });
      });
    });
    // Activate the default pane (color) so the overlay has something to
    // show the first time it opens.
    var firstPane = document.querySelector('.qc-pane[data-qc-pane="color"]');
    if (firstPane) firstPane.classList.add('is-active');
  }

  // Toggles `.qc-overflow-left` / `.qc-overflow-right` on the swatch
  // strip so the mobile edge-fade only renders on the side(s) that
  // actually have hidden content to scroll into:
  //   - At scroll start  → only right edge fades
  //   - Mid-scroll       → both edges fade
  //   - At scroll end    → only left edge fades
  //   - No overflow      → no fade at all
  // The CSS mask-image rules pick up these classes; without them the
  // swatch row renders with no mask. Tolerance of 1px guards against
  // sub-pixel rounding glitches that would otherwise flicker the fade.
  function updateSwatchesScrollState(el) {
    if (!el) return;
    var left  = el.scrollLeft > 1;
    var right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    el.classList.toggle('qc-overflow-left',  left);
    el.classList.toggle('qc-overflow-right', right);
  }

  // Generic — bind the same scroll-state toggling to any horizontally
  // scrolling element by ID. Used for both `#qc-swatches` (color tab)
  // and `#qc-fonts` (font tab on mobile). The CSS in
  // quick-customization-mobile.liquid hangs edge-fade masks off the
  // resulting `qc-overflow-left` / `qc-overflow-right` classes.
  function bindScrollOverflow(id) {
    var el = document.getElementById(id);
    if (!el || el._qcScrollBound) return;
    el._qcScrollBound = true;
    var sync = function () { updateSwatchesScrollState(el); };
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Translate vertical wheel input into horizontal scroll when this
    // row actually has overflow. Without this, hovering over the font
    // pill (or the mobile swatch strip on a desktop with a touchpad)
    // and using the mouse wheel scrolls the page underneath instead of
    // the row, which feels broken when there's clearly hidden content
    // visible behind the edge fade.
    //
    // We only preventDefault while there's still room to scroll in the
    // wheel direction — once the row is pinned at its left/right edge
    // the wheel passes through and the page scrolls normally, so the
    // shopper doesn't get stuck on a "trap" element.
    el.addEventListener('wheel', function (ev) {
      if (el.scrollWidth <= el.clientWidth) return;
      var dy = ev.deltaY;
      var dx = ev.deltaX;
      // If the gesture is already horizontal (trackpad two-finger
      // horizontal, or shift+wheel) the browser handles it natively —
      // leave it alone.
      if (Math.abs(dy) <= Math.abs(dx)) return;
      // deltaMode: 0 = pixels (trackpad / smooth mice), 1 = lines
      // (classic notched wheels), 2 = pages. Normalize to a px delta
      // so notched wheels still move the row a sensible amount.
      var multiplier = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? el.clientWidth : 1;
      var delta = dy * multiplier;
      var atStart = el.scrollLeft <= 0;
      var atEnd   = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
      el.scrollLeft += delta;
      ev.preventDefault();
    }, { passive: false });

    // Initial check + re-checks after layout settles (fonts load,
    // mobile sheet expands and the row's clientWidth jumps, etc.).
    sync();
    setTimeout(sync, 50);
    setTimeout(sync, 250);
  }

  function bindSwatchesScroll() { bindScrollOverflow('qc-swatches'); }
  function bindFontsScroll()    { bindScrollOverflow('qc-fonts'); }

  // Mobile "+ Add my own colors" launcher: hides itself on click and
  // unhides the chip strip so it takes the launcher's place. The chip
  // strip is gated by two things — the section's initial `[hidden]`
  // attribute (cleared on first swatch pick) AND the mobile sibling
  // rule that keeps it suppressed while the launcher is visible — so
  // we drop both gates here in case the shopper opts in BEFORE picking
  // a swatch (chips render in their default empty state).
  function bindChipsLauncher() {
    var launcher = document.getElementById('qc-chips-launcher');
    if (!launcher) return;
    launcher.addEventListener('click', function () {
      launcher.hidden = true;
      launcher.setAttribute('aria-expanded', 'true');
      var chipsEl = document.getElementById('qc-chips');
      if (chipsEl) chipsEl.hidden = false;
    });
  }

  function bindLauncher() {
    var sidebar  = getSidebar();
    if (!sidebar) return;
    var launcher = sidebar.querySelector('[class*="qc-launcher-"]');
    if (!launcher) return;

    // Helper: switch the mobile bottom-sheet between collapsed / expanded.
    // Re-used by the launcher click + the IntersectionObserver below.
    function setOpen(open) {
      sidebar.classList.toggle('is-mobile-open', open);
      launcher.classList.toggle('is-open', open);
      launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    // Tracks whether the shopper has manually collapsed the sheet via
    // the pill. The auto-open path (IntersectionObserver) honors this:
    // once they close it, we don't re-open on subsequent scroll-ins.
    // Tapping the pill again to re-open clears the flag, so future
    // scrolls back into the grid resume the default-open behavior.
    var userClosed = false;

    // The launcher is now INSIDE the sidebar (as its first child), so
    // the pill + collapsed/expanded panel render as a single bottom-
    // anchored unit. Click toggles `.is-mobile-open` on the sidebar:
    //   - collapsed: only the pill is visible (sidebar width = 144px)
    //   - expanded: sidebar widens, panel content (tabs + active pane)
    //               reveals below the pill, anchored to viewport bottom
    launcher.addEventListener('click', function () {
      var open = !sidebar.classList.contains('is-mobile-open');
      setOpen(open);
      // Closing via the pill is sticky across scroll-out/scroll-in
      // (see userClosed comment above). Opening clears the stickiness.
      userClosed = !open;

      // The horizontally-scrolling rows (swatches + fonts) change
      // clientWidth drastically when the sheet expands (collapsed
      // 144px → expanded 100vw), so their overflow-left / overflow-
      // right state needs to recompute. Same again 250ms later so the
      // sheet's `bottom: 0` transition has finished.
      if (open) {
        ['qc-swatches', 'qc-fonts'].forEach(function (id) {
          var el = document.getElementById(id);
          if (!el) return;
          updateSwatchesScrollState(el);
          setTimeout(function () { updateSwatchesScrollState(el); }, 250);
        });
      }
    });

    // The pill should only appear when the products grid is on screen
    // — otherwise it floats over hero / FAQ / footer content. We toggle
    // a `.qc-mobile-out-of-view` class (NOT the `hidden` attribute) so
    // the rule is scoped via the mobile media query and the sidebar
    // stays visible on desktop where it occupies a real grid column.
    if (typeof IntersectionObserver === 'undefined') return;

    var productsGrid = document.querySelector('.collection-grid-section .product-grid')
      || document.querySelector('.collection-grid-section');
    if (!productsGrid) return;

    // Start hidden — IntersectionObserver fires immediately with the
    // current state, so if the grid is in view at page load the
    // sidebar will un-hide on the first tick.
    sidebar.classList.add('qc-mobile-out-of-view');
    var obs = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry.isIntersecting) {
        sidebar.classList.remove('qc-mobile-out-of-view');
        // Default the bottom-sheet to OPEN every time the products
        // grid scrolls into view — unless the shopper has manually
        // collapsed it via the pill, in which case respect that
        // intent and leave it closed.
        if (!userClosed) {
          setOpen(true);
          // The horizontally-scrolling rows (swatches + fonts) need
          // an overflow-state recompute now that they have a non-zero
          // clientWidth (the sheet just expanded from 0 → viewport).
          ['qc-swatches', 'qc-fonts'].forEach(function (id) {
            var el = document.getElementById(id);
            if (!el) return;
            updateSwatchesScrollState(el);
            setTimeout(function () { updateSwatchesScrollState(el); }, 250);
          });
        }
      } else {
        sidebar.classList.add('qc-mobile-out-of-view');
        // The whole sidebar is hidden via the out-of-view class, so
        // we deliberately leave the open/closed state untouched —
        // that way scrolling back in restores exactly what the
        // shopper last saw (auto-open by default, or stays closed if
        // they manually collapsed).
      }
    }, { threshold: 0.05 });

    obs.observe(productsGrid);
  }

  function fetchCollectionConfig(code) {
    // Base host + env-correct /api/<apiPath> come from snippets/app-config.liquid
    // (window.APP_CONFIG). Fallback only for local previews where it never rendered.
    var base = (window.APP_CONFIG && window.APP_CONFIG.endpoints && window.APP_CONFIG.endpoints.collectionQuickCustomization) ||
               'https://scripts.squadmojo-dev.com/api/squad/rest/collection-quick-customization';
    var url = base + '/' + encodeURIComponent(code);

    return fetch(url)
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function ()    { return null; });
  }

  // ─── Init ────────────────────────────────────────────────────────────────
  function init() {
    relocatePanelIntoGrid();

    var container = document.getElementById('qc-swatches');
    if (!container) return;

    var sidebar = getSidebar();
    var code    = sidebar ? (sidebar.getAttribute('data-collection-code') || '') : '';
    if (!code) { hidePanel(); return; }

    bindSwatchClicks(container);

    var fontsEl = document.getElementById('qc-fonts');
    if (fontsEl) bindFontClicks(fontsEl);

    // Mobile pattern (< 880px viewport): the floating "Customize Now"
    // launcher + tab nav. Bindings are inert above 880px (no launcher
    // visible, tabs hidden by CSS, all panes shown together) so it's
    // safe to wire them on every page load.
    bindTabClicks();
    bindLauncher();
    bindChipsLauncher();
    bindChipClicks();

    fetchCollectionConfig(code).then(function (data) {
      var allThemes = (data && data.colorThemes) || [];
      if (!allThemes.length) { hidePanel(); return; }

      // Render every theme the API returns — the grid wraps to extra
      // rows on desktop (6 cols), and scrolls horizontally on mobile.
      // Reorder to match the admin-curated order in data.colorThemeIds
      // (drag-and-drop order set in the dashboard, with the configured
      // default at position 0).
      var themes = sortByIdOrder(allThemes, (data && data.colorThemeIds) || []);
      // wire scroll-state listener after first paint (cheap no-op on
      // desktop where the row is a grid with no horizontal overflow).
      bindSwatchesScroll();

      // Stash the API-declared default theme for future use (today: read
      // only for symmetry with apiDefaultFontPair, may drive a future
      // "reset to default" affordance). We deliberately do NOT seed
      // state.bannerTheme — the page should render exactly as if the
      // picker wasn't there until the shopper clicks a color.
      apiDefaultTheme = null;
      if (data.defaultColorThemeId) {
        for (var ti = 0; ti < themes.length; ti++) {
          if (themes[ti].id === data.defaultColorThemeId) {
            apiDefaultTheme = themes[ti];
            break;
          }
        }
      }
      if (!apiDefaultTheme) apiDefaultTheme = themes[0];

      // Picker UI: paint the swatches but mark nothing active.
      renderSwatches(container, themes);

      // Font pairs: stay hidden when the API returns an empty list. When
      // present, render the picker (no active button) and preload the
      // Google Fonts stylesheets (for both halves of every pair) so the
      // swap is snappy when the shopper eventually picks one. The
      // API-declared default pair is stashed in apiDefaultFontPair —
      // selectDefaultFont() promotes it to active on first shopper
      // interaction (typing a team name or clicking a color in the
      // banner).
      //
      // No id-based reorder here: the API now returns `fontPairs[]` in
      // the admin-curated order (drag-and-drop in the dashboard's font
      // pair modal), and pairs aren't individually identified — they're
      // tuples, so the curated array order IS the order.
      var fontPairs = (data && data.fontPairs) || [];
      apiDefaultFontPair = null;
      if (fontsEl && fontPairs.length) {
        injectFontFaces(fontPairs);
        renderFonts(fontsEl, fontPairs);
        fontsEl.hidden = false;
        // Wire scroll-state listener on the font row so the edge-fade
        // reads its overflow on whichever side has more to scroll.
        // Used by both the mobile font-tile row and the desktop font
        // pill (both horizontally scrollable when the font list is
        // longer than the row's visible width).
        bindFontsScroll();

        // First pair is the admin-configured default — the dashboard's
        // "default font" UI was replaced when fonts moved to pairs, and
        // ordering is now the single source of truth (the editor saves
        // the chosen default at index 0).
        apiDefaultFontPair = fontPairs[0];
      }

      // If the shopper has already armed the auto-load pipeline before
      // this API fetch resolved (typed/focused early), promote the API
      // default font now so the just-painted overlays adopt it. We key
      // off `state.armed` rather than `state.currentText` so cookie
      // restore alone — without any interaction this visit — does NOT
      // promote the font. The arm() helper in team-design.text-render.js
      // covers the symmetric case where the API resolved first and the
      // shopper arms later.
      if (readState().armed) selectDefaultFont();
    });
  }

  // Public hook used by text.js to promote the API default font when the
  // shopper starts typing a team name. See selectDefaultFont() for the
  // no-op conditions.
  window.CollectionQuickCustomization = window.CollectionQuickCustomization || {};
  window.CollectionQuickCustomization.selectDefaultFont = selectDefaultFont;

  function tryInit(retries) {
    var container = document.getElementById('qc-swatches');
    if (!container) return;
    var firstCard = document.querySelector('.card-wrapper[data-sku]');
    // The grid may be hydrated by Shopify section rendering after parse — poll
    // briefly until a card appears or we give up. We still wait for at least
    // one card so applyThemeToCard has a target when the user picks a swatch.
    if (!firstCard) {
      if (retries > 0) setTimeout(function () { tryInit(retries - 1); }, 200);
      return;
    }
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { tryInit(20); });
  } else {
    tryInit(20);
  }
})();
